import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router";

// PrimeReact
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

// Toolkit
import { themeClasses as T, C } from "sj-ui-toolkit/src/atlas/utils/themeClasses";
import { FullScreenLoader } from "sj-ui-toolkit/src/components";
import { fetchTournamentById } from "sj-ui-toolkit/src/api";
import { LearnAppRoutes, buildRoute } from "sj-ui-toolkit/src/constants/routes";
import { useSmartNavigation } from "sj-ui-toolkit/src/helpers/navigation";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { PageBreadcrumb } from "sj-ui-toolkit/src/atlas/components";
import { useHideTimeSpent } from "sj-ui-toolkit/src/integrations";
import { EMAIL_REGEX } from "sj-ui-toolkit/src/utils/string";

// Component dependencies
import S from "./TournamentPage.module.scss";
import { calculateTournamentStatus, exerciseStatusText } from "./tournamentUtil";

// Contact info is a free-form string (often one or more emails). Preserve the
// author's text — including line breaks via `white-space: pre-line` — while
// turning any email addresses into mailto links. EMAIL_REGEX has one capture
// group, so split() keeps the matched emails at the odd indices.
function renderContactInfo(contactInfo) {
  return contactInfo.split(EMAIL_REGEX).map((part, index) => {
    if (!part) return null;
    return index % 2 === 1 ? (
      <a key={index} href={`mailto:${part}`} className={T.linkPrimary}>
        {part}
      </a>
    ) : (
      <span key={index}>{part}</span>
    );
  });
}

// Main component
export default function TournamentPage() {
  const { navigateTo } = useSmartNavigation();
  const { tournamentId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [tournament, setTournament] = useState(null);
  const hideTimeSpent = useHideTimeSpent();

  useBreadcrumb(
    tournament
      ? {
          label: tournament.name || tournament.title,
          route: `/tournament/${tournamentId}`,
          display: "page",
        }
      : null
  );

  useEffect(() => {
    // Fetch tournament data
    const fetchTournament = async () => {
      setIsLoading(true);
      try {
        const data = await fetchTournamentById(tournamentId);
        setTournament(data);
      } catch (error) {
        console.error("Error fetching tournament:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (tournamentId) {
      fetchTournament();
    }
  }, [tournamentId]);

  // Compute the overall tournament status
  const tournamentStatus = useMemo(() => {
    if (!tournament) return null;
    return calculateTournamentStatus(
      tournament.startDate,
      tournament.finishDate,
      tournament.enrolled
    );
  }, [tournament]);

  // Compute completed and active exercises
  const completedExercises = useMemo(() => {
    if (!tournament?.exercises) return [];
    return tournament.exercises.filter(exercise => exercise.status === "passed");
  }, [tournament?.exercises]);

  const activeExercises = useMemo(() => {
    if (!tournament?.exercises) return [];
    return tournament.exercises.filter(exercise => exercise.status !== "passed");
  }, [tournament?.exercises]);

  const statusColumnBody = rowData => {
    if (rowData.status === "passed") {
      return exerciseStatusText(rowData.status);
    } else {
      const route = buildRoute(LearnAppRoutes.routes.tournaments.routes.content.route, {
        tournamentId,
        contentType: rowData.type,
        contentId: rowData.id,
      });
      return (
        <Button
          disabled={tournament.expired}
          label={exerciseStatusText(rowData.status)}
          onClick={() => navigateTo(route)}
        />
      );
    }
  };

  if (isLoading) {
    return <FullScreenLoader />;
  }

  if (!tournament) {
    return (
      <div
        className={C(T.largePaddedContainer, "flex flex-col items-center justify-center h-full")}
        role="alert"
      >
        <h1>Tournament not found</h1>
        <p>The tournament you're looking for doesn't exist.</p>
      </div>
    );
  }

  // Optional, free-form fields — only render their card when the admin set a value.
  const prizes = tournament.prizes?.trim();
  const rules = tournament.rules?.trim();
  const contactInfo = tournament.contactInfo?.trim();

  return (
    <div>
      <PageBreadcrumb />
      <div className={C(S.tournamentPage, "pt-2 pb-10")}>
        <div className={S.tournamentSubheader}>
          <Tag
            value={tournamentStatus.statusText}
            severity="secondary"
            rounded={true}
            aria-label={`Tournament status: ${tournamentStatus.statusText}`}
          />
        </div>

        {/* Display status of the tournament, both for the user and overall */}
        <div className={C(S.tournamentHeader, "mt-10")}>
          <section className={S.overview} aria-labelledby="tournament-title">
            <h1 id="tournament-title" className={C("atlas-h3", "mb-4")}>
              {tournament.title || tournament.name}
            </h1>
            <p className="body-lg ">{tournament.summary}</p>

            <div className={C(S.tournamentProgress, "mt-9")}>
              <div className={S.section}>
                <p className={C(S.mutedText, "atlas-h5 uppercase")}>Points Earned</p>
                <p className="atlas-h2">{tournament.tournamentUserPoints}</p>
              </div>

              <div className={S.section}>
                <p className={C(S.mutedText, "atlas-h5 uppercase")}>Exercises Completed</p>
                <p className="atlas-h2">{tournament.completedExercises}</p>
                <p className={S.mutedText}>/ {tournament.exercisesCount} total exercises</p>
              </div>

              {!hideTimeSpent && (
                <div className={S.section}>
                  <p className={C(S.mutedText, "atlas-h5 uppercase")}>Time Spent</p>
                  <p className="atlas-h2">{tournament.time}</p>
                  <p className={S.mutedText}>{tournament.remainingTime} remaining to complete</p>
                </div>
              )}
            </div>
          </section>
          <section className={S.tournamentLeaderboard} aria-labelledby="leaderboard-heading">
            <h2 id="leaderboard-heading" className={C("atlas-h3", "mb-4")}>
              Leaderboard
            </h2>
            <DataTable
              pt={{ table: { "aria-labelledby": "leaderboard-heading" } }}
              value={tournament.tournamentParticipants.leaders}
              className="p-datatable-sm"
              size="small"
              paginator
              rows={10}
              emptyMessage="No tournament standings available."
            >
              <Column field="score" header="Rank" />
              <Column field="name" header="Name" />
              <Column field="completedExercises" header="Completed Exercises" />
              <Column field="rating" header="Total Points" />
              {!hideTimeSpent && <Column field="time" header="Total Time" />}
            </DataTable>
          </section>
        </div>

        <div className={C(T.bgElevation1, "p-10 mt-15")}>
          {/* Display the list of exercises in the tournament */}
          <section aria-labelledby="remaining-exercises-heading">
            <h2 id="remaining-exercises-heading" className={C(S.tableHeader, "atlas-h4")}>
              Remaining Exercises
            </h2>
            <DataTable
              pt={{ table: { "aria-labelledby": "remaining-exercises-heading" } }}
              value={activeExercises}
              className="p-datatable-sm"
              size="small"
              paginator
              rows={10}
              emptyMessage="No exercises remaining."
            >
              <Column field="name" header="Name" />
              <Column field="type" header="Type" />
              <Column field="points" header="Points Earned" />
              {!hideTimeSpent && <Column field="time" header="Time Spent" />}
              <Column field="status" header="Status" body={statusColumnBody} />
            </DataTable>
          </section>

          {/* Completed exercises */}
          <section
            className={C(S.exerciseList, "mt-15")}
            aria-labelledby="completed-exercises-heading"
          >
            <h2 id="completed-exercises-heading" className={C(S.tableHeader, "atlas-h4")}>
              Completed Exercises
            </h2>
            <DataTable
              pt={{ table: { "aria-labelledby": "completed-exercises-heading" } }}
              value={completedExercises}
              className="p-datatable-sm"
              size="small"
              paginator
              rows={10}
              emptyMessage="No completed exercises yet."
            >
              <Column field="name" header="Name" />
              <Column field="type" header="Type" />
              <Column field="points" header="Points Earned" />
              {!hideTimeSpent && <Column field="time" header="Time Spent" />}
              <Column
                field="status"
                header="Status"
                body={rowData => exerciseStatusText(rowData.status)}
              />
            </DataTable>
          </section>
        </div>

        <section className={C(S.tournamentInfo, "mt-15")} aria-label="Tournament information">
          {rules && (
            <section className={C(T.bgElevation1, S.infoCard)} aria-labelledby="rules-heading">
              <div className={S.infoCardHeader}>
                <i
                  className={C("pi pi-flag", T.bgElevation2, T.textPrimary, S.infoCardIcon)}
                  aria-hidden="true"
                />
                <h2 id="rules-heading" className="atlas-h4">
                  Rules
                </h2>
              </div>
              <p className={S.infoCardBody}>{rules}</p>
            </section>
          )}

          <section className={C(T.bgElevation1, S.infoCard)} aria-labelledby="scoring-heading">
            <div className={S.infoCardHeader}>
              <i
                className={C("pi pi-calculator", T.bgElevation2, T.textPrimary, S.infoCardIcon)}
                aria-hidden="true"
              />
              <h2 id="scoring-heading" className="atlas-h4">
                Scoring
              </h2>
            </div>
            <ul className={S.infoCardList}>
              <li>
                Each activity is worth 100-200 possible points. The lowest you can score in an
                activity is 10 points.
              </li>
              <li>Points are deducted for each hint used and each incorrect code submission.</li>
              {!hideTimeSpent && (
                <li>
                  If there is a tie, the participant with the least amount of time spent in the
                  tournament will win.
                </li>
              )}
              <li>
                Video lessons must be completed with a score of 80% or above. Additional attempts
                will deduct 10 points from your score.
              </li>
              <li>
                If a video lesson contains a coding exercise, the coding exercise must also be
                completed to successfully complete the lesson.
              </li>
              <li>Hints used will deduct 5 points. The final hint will deduct 10 points.</li>
            </ul>
          </section>

          {contactInfo && (
            <section className={C(T.bgElevation1, S.infoCard)} aria-labelledby="contact-heading">
              <div className={S.infoCardHeader}>
                <i
                  className={C("pi pi-envelope", T.bgElevation2, T.textPrimary, S.infoCardIcon)}
                  aria-hidden="true"
                />
                <h2 id="contact-heading" className="atlas-h4">
                  Contact
                </h2>
              </div>
              <div className={C(S.infoCardBody, S.contactList)}>
                {renderContactInfo(contactInfo)}
              </div>
            </section>
          )}

          {prizes && (
            <section className={C(T.bgElevation1, S.infoCard)} aria-labelledby="prizes-heading">
              <div className={S.infoCardHeader}>
                <i
                  className={C("pi pi-trophy", T.bgElevation2, T.textPrimary, S.infoCardIcon)}
                  aria-hidden="true"
                />
                <h2 id="prizes-heading" className="atlas-h4">
                  Prizes
                </h2>
              </div>
              <p className={S.infoCardBody}>{prizes}</p>
            </section>
          )}
        </section>
      </div>
    </div>
  );
}
